// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rediger › Geometri's Snit (frontend-u0o.7, slice S7): a vertical section
 * along a cut line A–A on the plan. Here the pure part — the line's frame
 * (s metres along A → B, off metres to its left), the cloud's points in the
 * cut band as (s, z), what the cut crosses (walls to their thickness with
 * the openings it passes through, slabs, rooms with their ceiling profile,
 * the storey bands), the default cut for a storey and the cut through an
 * element, the height histogram of the cloud with its peaks (floors and
 * ceilings: what a storey height snaps to) and the view's fit. World
 * coordinates throughout. Pure: no React, no DOM.
 */

import type { CeilingPlane, PlanXY } from '../api/types';
import type { GeometriModel } from './geometri';
import type { XY } from './planWalls';

/** A cut: the line A–A (world x/y) and the depth of the band whose points it shows (metres, both sides together). */
export interface SectionLine {
  a: XY;
  b: XY;
  depth: number;
}

/** The band a cut shows the cloud of, metres (both sides together). */
export const DEFAULT_SECTION_DEPTH = 0.3;

export function sectionLength(line: SectionLine): number {
  return Math.hypot(line.b[0] - line.a[0], line.b[1] - line.a[1]);
}

/** Point (x, y) in the cut's frame: `s` metres along A → B from A, `off` metres to its left. */
export function toSection(line: SectionLine, x: number, y: number): { s: number; off: number } {
  const L = sectionLength(line);
  if (!(L > 1e-9)) return { s: 0, off: 0 };
  const dx = (line.b[0] - line.a[0]) / L;
  const dy = (line.b[1] - line.a[1]) / L;
  const vx = x - line.a[0];
  const vy = y - line.a[1];
  return { s: vx * dx + vy * dy, off: dx * vy - dy * vx };
}

/** The world point `s` metres along the cut. */
export function sectionPoint(line: SectionLine, s: number): XY {
  const L = sectionLength(line);
  const t = L > 1e-9 ? s / L : 0;
  return [line.a[0] + (line.b[0] - line.a[0]) * t, line.a[1] + (line.b[1] - line.a[1]) * t];
}

/**
 * The cloud's points (xyz, stride 3) within half the depth of the line and
 * between its ends, as (s, z) pairs.
 */
export function sliceCloud(xyz: ArrayLike<number>, line: SectionLine): Float32Array {
  const L = sectionLength(line);
  if (!(L > 1e-9)) return new Float32Array(0);
  const dx = (line.b[0] - line.a[0]) / L;
  const dy = (line.b[1] - line.a[1]) / L;
  const half = line.depth / 2;
  const out: number[] = [];
  for (let i = 0; i + 2 < xyz.length; i += 3) {
    const vx = xyz[i] - line.a[0];
    const vy = xyz[i + 1] - line.a[1];
    const s = vx * dx + vy * dy;
    if (s < 0 || s > L) continue;
    if (Math.abs(dx * vy - dy * vx) > half) continue;
    out.push(s, xyz[i + 2]);
  }
  return Float32Array.from(out);
}

// ------------------------------------------------------- intersections --

/** Where segment a → b crosses each edge of `ring`, as fractions of a → b (0..1 and beyond). */
function crossings(a: XY, b: XY, ring: readonly PlanXY[]): number[] {
  const out: number[] = [];
  const rx = b[0] - a[0];
  const ry = b[1] - a[1];
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const p = ring[j];
    const q = ring[i];
    const ex = q[0] - p[0];
    const ey = q[1] - p[1];
    const den = rx * ey - ry * ex;
    if (Math.abs(den) < 1e-12) continue;
    const wx = p[0] - a[0];
    const wy = p[1] - a[1];
    const t = (wx * ey - wy * ex) / den;
    const u = (wx * ry - wy * rx) / den;
    // Half-open on the edge, so a vertex the line passes through counts once.
    if (u >= 0 && u < 1) out.push(t);
  }
  return out;
}

/**
 * The stretches of segment a → b inside polygon {outer, holes} (even-odd:
 * holes are cut out), as [s0, s1] metres from a, clipped to the segment.
 */
export function lineRingIntervals(a: XY, b: XY, polygon: { outer: readonly PlanXY[]; holes?: readonly (readonly PlanXY[])[] }): [number, number][] {
  const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (!(L > 1e-9) || polygon.outer.length < 3) return [];
  const ts = [polygon.outer, ...(polygon.holes ?? [])].flatMap((r) => (r.length >= 3 ? crossings(a, b, r) : [])).sort((x, y) => x - y);
  const out: [number, number][] = [];
  for (let i = 0; i + 1 < ts.length; i += 2) {
    const s0 = Math.max(0, ts[i]) * L;
    const s1 = Math.min(1, ts[i + 1]) * L;
    if (s1 - s0 > 1e-6) out.push([s0, s1]);
  }
  return out;
}

// -------------------------------------------------------------- ceiling --

/** A plane's z at (x, y): at[2] + slope · (xy − at.xy). */
export function planeZ(p: CeilingPlane, x: number, y: number): number {
  const [gx, gy] = p.slope ?? [0, 0];
  return p.at[2] + gx * (x - p.at[0]) + gy * (y - p.at[1]);
}

/** A room's ceiling at (x, y): the lowest of its flat `z_ceiling` and every plane (ReUseX `ceiling_at`). */
export function ceilingAt(planes: readonly CeilingPlane[], zCeiling: number, x: number, y: number): number {
  let z = zCeiling;
  for (const p of planes) z = Math.min(z, planeZ(p, x, y));
  return z;
}

// ------------------------------------------------------------- the cut --

export interface SectionWall {
  guid: string;
  storeyId: number | null;
  s0: number;
  s1: number;
  z0: number;
  z1: number;
}

export interface SectionOpening {
  guid: string;
  wallGuid: string;
  kind: string;
  s0: number;
  s1: number;
  sill: number;
  head: number;
}

export interface SectionSlab {
  guid: string;
  kind: string;
  storeyId: number | null;
  s0: number;
  s1: number;
  z0: number;
  z1: number;
}

export interface SectionSpace {
  guid: string;
  storeyId: number | null;
  s0: number;
  s1: number;
  floor: number;
  /** The ceiling along the stretch, (s, z), every 10 cm and at both ends. */
  ceiling: [number, number][];
  /** Flat (no planes). */
  flat: boolean;
}

export interface SectionStorey {
  id: number;
  guid: string;
  name: string;
  z0: number;
  z1: number;
  isGround: boolean;
}

export interface SectionCut {
  walls: SectionWall[];
  openings: SectionOpening[];
  slabs: SectionSlab[];
  spaces: SectionSpace[];
  storeys: SectionStorey[];
}

const PROFILE_STEP = 0.1;
const PROFILE_MAX = 200;

/** A wall's footprint: its axis widened to its thickness (a sliver when unknown). */
function wallRing(start: XY, end: XY, thickness: number): PlanXY[] {
  const L = Math.hypot(end[0] - start[0], end[1] - start[1]);
  if (!(L > 1e-9)) return [];
  const h = Math.max(thickness, 0.02) / 2;
  const nx = (-(end[1] - start[1]) / L) * h;
  const ny = ((end[0] - start[0]) / L) * h;
  return [
    [start[0] + nx, start[1] + ny],
    [end[0] + nx, end[1] + ny],
    [end[0] - nx, end[1] - ny],
    [start[0] - nx, start[1] - ny],
  ];
}

/**
 * What the cut crosses, every storey's: walls (cut to their thickness and
 * height), the openings whose span the cut passes through on a crossed
 * wall, slabs, rooms (their floor and ceiling profile) and the storey
 * bands, lowest first.
 */
export function sectionCut(model: GeometriModel, line: SectionLine): SectionCut {
  const { a, b } = line;
  const walls: SectionWall[] = [];
  const openings: SectionOpening[] = [];
  const byWall = new Map<string, NonNullable<GeometriModel['openings']>[number][]>();
  for (const o of model.openings ?? []) byWall.set(o.wall_guid, [...(byWall.get(o.wall_guid) ?? []), o]);
  for (const w of model.walls ?? []) {
    const ring = wallRing(w.start, w.end, w.thickness ?? 0);
    const L = Math.hypot(w.end[0] - w.start[0], w.end[1] - w.start[1]);
    for (const [s0, s1] of lineRingIntervals(a, b, { outer: ring })) {
      walls.push({ guid: w.guid, storeyId: w.storey_id, s0, s1, z0: w.z_base, z1: w.z_top });
      // Where along the wall the cut crosses it (the stretch's middle on the wall's axis).
      const mid = sectionPoint(line, (s0 + s1) / 2);
      const t = L > 1e-9 ? ((mid[0] - w.start[0]) * (w.end[0] - w.start[0]) + (mid[1] - w.start[1]) * (w.end[1] - w.start[1])) / L : 0;
      for (const o of byWall.get(w.guid) ?? []) {
        if (t < Math.min(o.s_start, o.s_end) || t > Math.max(o.s_start, o.s_end)) continue;
        openings.push({ guid: o.guid, wallGuid: w.guid, kind: o.kind, s0, s1, sill: o.z_sill, head: o.z_head });
      }
    }
  }
  const slabs: SectionSlab[] = [];
  for (const sl of model.slabs ?? []) {
    for (const [s0, s1] of lineRingIntervals(a, b, sl.polygon)) {
      slabs.push({ guid: sl.guid, kind: sl.kind, storeyId: sl.storey_id, s0, s1, z0: sl.z_base, z1: sl.z_top });
    }
  }
  const spaces: SectionSpace[] = [];
  for (const sp of model.spaces ?? []) {
    const planes = sp.ceiling_planes ?? [];
    for (const [s0, s1] of lineRingIntervals(a, b, sp.polygon)) {
      const n = Math.min(PROFILE_MAX, Math.max(1, Math.ceil((s1 - s0) / PROFILE_STEP)));
      const ceiling: [number, number][] = [];
      for (let i = 0; i <= n; i += 1) {
        const s = s0 + ((s1 - s0) * i) / n;
        const p = sectionPoint(line, s);
        ceiling.push([s, ceilingAt(planes, sp.z_ceiling, p[0], p[1])]);
      }
      spaces.push({ guid: sp.guid, storeyId: sp.storey_id, s0, s1, floor: sp.z_floor, ceiling, flat: planes.length === 0 });
    }
  }
  const storeys = [...(model.storeys ?? [])]
    .sort((x, y) => x.z_min - y.z_min || x.id - y.id)
    .map((s) => ({ id: s.id, guid: s.guid, name: s.name, z0: s.z_min, z1: s.z_max, isGround: s.is_ground === true }));
  return { walls, openings, slabs, spaces, storeys };
}

// --------------------------------------------------------- default cuts --

/** How far a default cut runs past what it cuts, metres. */
const CUT_OVERRUN = 1;

/** Where across the box a cut may run, as fractions from the middle out. */
const CUT_SPOTS = [0.5, 0.45, 0.55, 0.4, 0.6, 0.35, 0.65, 0.3, 0.7, 0.25, 0.75];
/** A cut that runs inside one wall for longer than this (metres) shows a wall's length, not a section. */
const ALONG_WALL = 0.5;

/**
 * A cut along the longer side of a footprint's box, through its middle (or
 * the nearest spot off it that does not run inside a wall of `walls`),
 * `overrun` past both ends.
 */
function cutAlongBox(points: readonly PlanXY[], overrun: number, walls: GeometriModel['walls'] = null, across = false, depth = DEFAULT_SECTION_DEPTH): SectionLine | null {
  if (points.length === 0) return null;
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const x0 = Math.min(...xs);
  const x1 = Math.max(...xs);
  const y0 = Math.min(...ys);
  const y1 = Math.max(...ys);
  const r = (v: number) => Math.round(v * 1e6) / 1e6;
  const alongX = x1 - x0 >= y1 - y0 !== across;
  const make = (f: number): SectionLine =>
    alongX
      ? { a: [r(x0 - overrun), r(y0 + (y1 - y0) * f)], b: [r(x1 + overrun), r(y0 + (y1 - y0) * f)], depth }
      : { a: [r(x0 + (x1 - x0) * f), r(y0 - overrun)], b: [r(x0 + (x1 - x0) * f), r(y1 + overrun)], depth };
  const runsInWall = (l: SectionLine) =>
    (walls ?? []).some((w) => lineRingIntervals(l.a, l.b, { outer: wallRing(w.start, w.end, w.thickness ?? 0) }).some(([s0, s1]) => s1 - s0 > ALONG_WALL));
  for (const f of CUT_SPOTS) {
    const l = make(f);
    if (!runsInWall(l)) return l;
  }
  return make(0.5);
}

/**
 * The cut a storey opens on (`storeyId`; null = the whole building): across
 * its walls' footprint (else its rooms', else its slabs') — the short side,
 * so the heights read at the largest scale — through the middle (stepped
 * aside when that runs inside a wall), a metre past both ends. Null when
 * there is nothing to cut.
 */
export function defaultSection(model: GeometriModel, storeyId: number | null): SectionLine | null {
  const on = <T extends { storey_id: number | null }>(rows: readonly T[] | null | undefined) => (rows ?? []).filter((r) => storeyId === null || r.storey_id === storeyId);
  // The cut shows every storey: it steps aside from a wall of any of them.
  const walls = model.walls ?? [];
  const wallPts = on(model.walls).flatMap((w) => [w.start, w.end]);
  if (wallPts.length > 0) return cutAlongBox(wallPts, CUT_OVERRUN, walls, true);
  const roomPts = on(model.spaces).flatMap((s) => s.polygon.outer);
  if (roomPts.length > 0) return cutAlongBox(roomPts, CUT_OVERRUN, walls, true);
  return cutAlongBox(on(model.slabs).flatMap((s) => s.polygon.outer), CUT_OVERRUN, walls, true);
}

/** Half the length of a cut across a wall, metres. */
const ACROSS = 2;

/**
 * The cut through one element: square across an opening's host at the
 * opening's middle (its sill and head in view), across a wall at its
 * middle, along a room's long side (its ceiling's profile), across a
 * slab. Null for an unknown one.
 */
export function sectionThrough(model: GeometriModel, guid: string): SectionLine | null {
  const across = (start: XY, end: XY, t: number): SectionLine | null => {
    const L = Math.hypot(end[0] - start[0], end[1] - start[1]);
    if (!(L > 1e-9)) return null;
    const d = [(end[0] - start[0]) / L, (end[1] - start[1]) / L];
    const c: XY = [start[0] + d[0] * t, start[1] + d[1] * t];
    // Left of the wall's direction to its right, so the cut looks along the wall's start → end.
    const n = [-d[1], d[0]];
    return { a: [c[0] + n[0] * ACROSS, c[1] + n[1] * ACROSS], b: [c[0] - n[0] * ACROSS, c[1] - n[1] * ACROSS], depth: DEFAULT_SECTION_DEPTH };
  };
  const o = model.openings?.find((r) => r.guid === guid);
  if (o) {
    const host = model.walls?.find((w) => w.guid === o.wall_guid);
    return host ? across(host.start, host.end, (o.s_start + o.s_end) / 2) : null;
  }
  const w = model.walls?.find((r) => r.guid === guid);
  if (w) return across(w.start, w.end, Math.hypot(w.end[0] - w.start[0], w.end[1] - w.start[1]) / 2);
  const sp = model.spaces?.find((r) => r.guid === guid);
  const storeyWalls = (id: number | null) => (model.walls ?? []).filter((x) => x.storey_id === id);
  if (sp) return cutAlongBox(sp.polygon.outer, 0.5, storeyWalls(sp.storey_id));
  const sl = model.slabs?.find((r) => r.guid === guid);
  if (sl) return cutAlongBox(sl.polygon.outer, 0.5, storeyWalls(sl.storey_id), true);
  return null;
}

// ------------------------------------------------------------ histogram --

export interface HeightHistogram {
  /** World z of the first bin's floor. */
  z0: number;
  /** Bin height, metres. */
  bin: number;
  counts: number[];
}

/** The cloud's heights in bins (default 5 cm) over their range (or `zMin`..`zMax`). */
export function heightHistogram(zs: ArrayLike<number>, opts: { bin?: number; zMin?: number; zMax?: number } = {}): HeightHistogram {
  const bin = opts.bin ?? 0.05;
  let lo = opts.zMin ?? Infinity;
  let hi = opts.zMax ?? -Infinity;
  if (opts.zMin === undefined || opts.zMax === undefined) {
    for (let i = 0; i < zs.length; i += 1) {
      const z = zs[i];
      if (!Number.isFinite(z)) continue;
      if (opts.zMin === undefined && z < lo) lo = z;
      if (opts.zMax === undefined && z > hi) hi = z;
    }
  }
  if (!(hi >= lo)) return { z0: 0, bin, counts: [] };
  const z0 = Math.floor(lo / bin) * bin;
  const n = Math.max(1, Math.floor((hi - z0) / bin) + 1);
  const counts = new Array<number>(n).fill(0);
  for (let i = 0; i < zs.length; i += 1) {
    const k = Math.floor((zs[i] - z0) / bin);
    if (k >= 0 && k < n) counts[k] += 1;
  }
  return { z0: Math.round(z0 * 1e6) / 1e6, bin, counts };
}

/**
 * The histogram's peaks — the floors and ceilings, where a scan's points
 * pile up — as bin-centre heights, lowest first: local maxima that stand
 * out from their surroundings (at least `prominence` times, default 1.8,
 * the mean of the bins 15–75 cm away), hold at least `minShare` of the
 * points (default 0.3 %), and lie at least `minGap` metres (default 0.25:
 * a slab's underside and the floor on it are two) from a stronger one.
 */
export function histogramPeaks(h: HeightHistogram, opts: { minShare?: number; minGap?: number; prominence?: number } = {}): number[] {
  const total = h.counts.reduce((a, b) => a + b, 0);
  if (total === 0) return [];
  const minCount = Math.max(3, (opts.minShare ?? 0.003) * total);
  const gap = Math.max(1, Math.round((opts.minGap ?? 0.25) / h.bin));
  const near = Math.max(1, Math.round(0.15 / h.bin));
  const far = Math.max(near + 1, Math.round(0.75 / h.bin));
  const prominence = opts.prominence ?? 1.8;
  const around = (i: number) => {
    let sum = 0;
    let n = 0;
    for (let d = near; d <= far; d += 1) {
      for (const k of [i - d, i + d]) {
        sum += h.counts[k] ?? 0;
        n += 1;
      }
    }
    return sum / n;
  };
  const order = h.counts.map((c, i) => [c, i] as const).filter(([c]) => c >= minCount).sort((x, y) => y[0] - x[0] || x[1] - y[1]);
  const taken: number[] = [];
  for (const [c, i] of order) {
    if (taken.some((j) => Math.abs(j - i) < gap)) continue;
    if (c < (h.counts[i - 1] ?? 0) || c < (h.counts[i + 1] ?? 0)) continue;
    if (c < prominence * around(i)) continue;
    taken.push(i);
  }
  return taken.sort((x, y) => x - y).map((i) => Math.round((h.z0 + (i + 0.5) * h.bin) * 1e6) / 1e6);
}

/** `z` snapped to the nearest of `targets` within `tol`, or null. */
export function snapHeight(z: number, targets: readonly number[], tol: number): number | null {
  let best: number | null = null;
  for (const t of targets) if (Math.abs(t - z) <= tol && (best === null || Math.abs(t - z) < Math.abs(best - z))) best = t;
  return best;
}

// ----------------------------------------------------------------- view --

/** A section view: screen x = ox + s·k, screen y = oy − z·k (px). */
export interface SectionView {
  k: number;
  ox: number;
  oy: number;
}

/** Fit `s` and `z` ranges into a w × h box with `pad` px all round, one scale both ways, centred. */
export function fitSection(sRange: [number, number], zRange: [number, number], w: number, h: number, pad: number): SectionView {
  const sw = Math.max(1e-6, sRange[1] - sRange[0]);
  const zh = Math.max(1e-6, zRange[1] - zRange[0]);
  const k = Math.max(1e-6, Math.min((w - 2 * pad) / sw, (h - 2 * pad) / zh));
  const ox = pad + (w - 2 * pad - sw * k) / 2 - sRange[0] * k;
  const oy = h - pad - (h - 2 * pad - zh * k) / 2 + zRange[0] * k;
  return { k, ox, oy };
}
